import { useEffect, useRef, useState } from 'react';
import { productoActivo } from '../lib/productoActivo.js';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Input,
  Dropdown,
  Combobox,
  Option,
  Switch,
  Field,
  MessageBar,
  MessageBarBody,
  MessageBarActions,
  Spinner,
  Text,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  makeStyles,
  tokens,
  useToastController,
  Toast,
  ToastTitle,
} from '@fluentui/react-components';
import { Save24Regular } from '@fluentui/react-icons';
import { AppTopBar } from '../components/AppTopBar.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { FieldHelp } from '../components/FieldHelp.js';
import { ApiClientError } from '../api/client.js';
import {
  getObraContext,
  putObraContext,
  type ObraContext,
} from '../api/apuIa.js';
import { getProject, type Project, type ProjectFile } from '../api/projects.js';
import { getSummary, regenerateSummary } from '../api/projectAnalysis.js';
import { decidirAnalisisAlEntrar } from '../lib/analisisAlEntrarAlContexto.js';
import { GLOBAL_TOASTER_ID } from '../components/ThemedProvider.js';
import { countries } from 'country-flag-icons';

// =============================================================
// País / moneda — listas ISO de la plataforma (sin tabla hardcodeada):
// países del paquete country-flag-icons (mismo que la página de catálogos),
// nombres vía Intl.DisplayNames, monedas vía Intl.supportedValuesOf.
// =============================================================
const regionNames = (() => {
  try { return new Intl.DisplayNames(['es'], { type: 'region' }); } catch { return null; }
})();
const currencyNames = (() => {
  try { return new Intl.DisplayNames(['es'], { type: 'currency' }); } catch { return null; }
})();
function nameOfRegion(cc: string): string {
  try { return regionNames?.of(cc.toUpperCase()) ?? cc.toUpperCase(); } catch { return cc.toUpperCase(); }
}
function nameOfCurrency(cc: string): string {
  try { return currencyNames?.of(cc.toUpperCase()) ?? cc.toUpperCase(); } catch { return cc.toUpperCase(); }
}
interface IsoOpt { code: string; name: string; }
const PAIS_OPTS: IsoOpt[] = (countries as string[])
  .map((cc) => ({ code: cc.toUpperCase(), name: nameOfRegion(cc) }))
  .sort((a, b) => a.name.localeCompare(b.name, 'es'));
const MONEDA_OPTS: IsoOpt[] = (() => {
  let codes: string[] = [];
  try { codes = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('currency') ?? []; } catch { codes = []; }
  return codes
    .map((cc) => ({ code: cc.toUpperCase(), name: nameOfCurrency(cc) }))
    .sort((a, b) => a.name.localeCompare(b.name, 'es'));
})();

const DEFAULT_PAIS = 'MX';
// LA MONEDA NO TIENE DEFAULT. Antes acá había 'MXN' y esta pantalla lo GUARDABA
// aunque nadie lo eligiera; con eso, una obra chilena quedaba declarada en pesos
// mexicanos y el generador de precios descartaba el único catálogo del cliente —el
// chileno, en CLP— por "estar en otra moneda": 17 de 17 análisis sin un solo precio
// del catálogo (medido 2026-08-14). Lo que se muestra ahora es una SUGERENCIA con
// evidencia: la moneda que declara el pliego o, si no la declara, la de los
// catálogos elegidos para la obra cuando todos coinciden (`project.moneda`). Si no
// hay evidencia, el campo queda vacío y hay que elegirlo: no se guarda sin él.

/** Combobox ISO con filtro por nombre o código. Guarda el código ISO en el
 *  contexto; muestra "Nombre (CC)". Reset del texto al perder foco. */
function IsoPicker(
  { value, defaultCode, options, onSelect, placeholder }: {
    value: string | undefined;
    defaultCode: string;
    options: IsoOpt[];
    onSelect: (code: string) => void;
    placeholder?: string;
  },
) {
  const current = (value || defaultCode).toUpperCase();
  const labelOf = (code: string) => {
    const o = options.find((x) => x.code === code);
    return o ? `${o.name} (${o.code})` : code;
  };
  const [text, setText] = useState(labelOf(current));
  useEffect(() => { setText(labelOf(current)); /* eslint-disable-next-line */ }, [current]);
  const q = text.trim().toLowerCase();
  const showingSelection = q === labelOf(current).toLowerCase();
  const filtered = (showingSelection || !q
    ? options
    : options.filter((o) => o.name.toLowerCase().includes(q) || o.code.toLowerCase().includes(q))
  ).slice(0, 60);
  return (
    <Combobox
      freeform
      value={text}
      selectedOptions={[current]}
      placeholder={placeholder}
      onChange={(e) => setText((e.target as HTMLInputElement).value)}
      onOptionSelect={(_e, d) => { if (d.optionValue) { onSelect(d.optionValue); setText(labelOf(d.optionValue)); } }}
      onBlur={() => setText(labelOf(current))}
    >
      {filtered.map((o) => (
        <Option key={o.code} value={o.code} text={`${o.name} (${o.code})`}>
          {o.name} ({o.code})
        </Option>
      ))}
    </Combobox>
  );
}

// =============================================================
// Estilos
// =============================================================

const useStyles = makeStyles({
  page: { minHeight: '100vh', background: 'var(--bg-page)' },
  container: { maxWidth: '920px', margin: '0 auto', padding: '24px 28px' },
  pageHead: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '20px', marginBottom: '20px', flexWrap: 'wrap' },
  // Acciones repetidas ARRIBA (visibles sin hacer scroll). Mismos handlers/estado
  // que la barra inferior (§ actions): no se duplica lógica, solo la posición.
  headActions: { display: 'flex', gap: '10px', flexShrink: 0, alignSelf: 'flex-start' },
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: '23px',
    fontWeight: 700,
    letterSpacing: '-0.3px',
    margin: 0,
    display: 'block',
    lineHeight: 1.2,
  },
  subtitle: {
    color: 'var(--text-secondary)',
    fontSize: '13px',
    marginTop: '5px',
    display: 'block',
    lineHeight: 1.5,
  },
  subtitleProject: {
    display: 'block',
    marginTop: '6px',
    color: 'var(--text-secondary)',
  },
  section: {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-lg)',
    padding: '18px 20px',
    marginBottom: '16px',
  },
  sectionTitle: { fontSize: '11px', fontWeight: 700, letterSpacing: '1.1px', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: '14px' },
  // `alignItems: 'start'` = cada celda se alinea al tope de su fila y NO se
  // estira a la altura del vecino. Sin esto, cuando la Moneda muestra su leyenda
  // de validación (más alta), la fila de 2 columnas se descuadra: la columna País
  // se estiraba para igualar la altura y el par dejaba de verse alineado.
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 24px', alignItems: 'start' },
  fullRow: { gridColumn: '1 / -1' },
  // Leyenda de badges (spec 2026-07-02) — franja fija arriba del formulario.
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px 26px',
    alignItems: 'center',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-lg)',
    padding: '12px 16px',
    marginBottom: '18px',
    fontSize: '12.5px',
    color: 'var(--text-secondary)',
  },
  legendItem: { display: 'flex', alignItems: 'center', gap: '8px' },
  legendBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 'var(--radius-circular)',
    fontSize: '9.5px',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  legendHelpIcon: {
    width: '20px',
    height: '20px',
    flexShrink: 0,
    borderRadius: '50%',
    border: '1.5px solid var(--border-default)',
    color: 'var(--text-tertiary)',
    fontSize: '12px',
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  actions: {
    position: 'sticky',
    bottom: 0,
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
    marginTop: '6px',
    padding: '16px 0 10px',
    borderTop: '1px solid var(--border-subtle)',
    background: 'var(--bg-page)',
  },
});

// =============================================================
// Página
// =============================================================

export function ObraContextPage() {
  const styles = useStyles();
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Volver a donde estaba el usuario (ej. la cotización) al guardar/cancelar.
  // Los callers pasan `state.returnTo`; sin él, cae al workspace del proyecto.
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo ?? `/project/${projectId}`;
  const { dispatchToast } = useToastController(GLOBAL_TOASTER_ID);

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [files, setFiles] = useState<ProjectFile[]>([]);
  // ci: lectura automática de los documentos al entrar (ver lib/analisisAlEntrarAlContexto).
  // 'leyendo' = el análisis corre; 'fallo' = no se pudo (mensaje + «Reintentar»).
  const [lectura, setLectura] = useState<'ninguna' | 'leyendo' | 'fallo'>('ninguna');
  const [lecturaError, setLecturaError] = useState<string | null>(null);
  const [recarga, setRecarga] = useState(0);
  // Una sola ejecución por entrada (React StrictMode dobla los efectos).
  const autoIntentado = useRef(false);
  const [context, setContext] = useState<ObraContext>({});
  const [hasExisting, setHasExisting] = useState(false);
  const [outdatedDialog, setOutdatedDialog] = useState<{ count: number } | null>(null);
  // El resumen del proyecto aún no se generó → no hay datos inferidos (ubicación,
  // clima, etc.). Se avisa dentro de la vista para que el usuario lo genere o
  // capture a mano. `obraAnalysis` viene en el proyecto (null si no se ha corrido).
  const analysisMissing = !!project && !(project as Project & { obraAnalysis?: unknown }).obraAnalysis;
  // MONEDA SUGERIDA, con evidencia y sin adivinar. El backend la resuelve igual que
  // el motor que cotiza: lo que declara el pliego/el contexto, y si no, la moneda de
  // los catálogos elegidos para la obra cuando todos los que la declaran coinciden.
  // `null` = no hay evidencia → el campo queda vacío y el usuario elige.
  const monedaSugerida = project?.moneda ?? null;
  const monedaDeCatalogos = project?.monedaOrigen === 'catalogos';
  const faltaMoneda = !context.moneda && !monedaSugerida;

  const recargaPrevia = useRef(0);
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    if (recarga === 0) setLoading(true);
    Promise.allSettled([
      getProject(projectId),
      getObraContext(projectId).catch((e) => {
        if (e instanceof ApiClientError && e.status === 404) return null;
        throw e;
      }),
    ]).then(([pRes, cRes]) => {
      if (cancelled) return;
      const projectValue = pRes.status === 'fulfilled' && pRes.value ? pRes.value.project : null;
      if (projectValue) setProject(projectValue);
      if (pRes.status === 'fulfilled' && pRes.value) setFiles(pRes.value.files ?? []);
      if (recargaPrevia.current > 0) {
        // Recarga tras terminar la lectura: no se pisa lo que la persona ya escribió.
        if (projectValue) aplicarAnalisisNuevo(projectValue);
        recargaPrevia.current = 0;
        setLoading(false);
        return;
      }

      if (cRes.status === 'fulfilled' && cRes.value) {
        // Se conserva el contexto guardado TAL CUAL. Solo se recalculan las
        // etiquetas "inferido" para que se muestren bien. El refresco (rehacer con
        // el nuevo análisis) se decide al REGENERAR el resumen, no aquí.
        const savedCtx = cRes.value.context ?? {};
        const meta = (savedCtx._meta ?? {}) as { deducido_de_obra_analysis?: string[]; override_usuario?: string[]; version_form?: string };
        const overrideSet = new Set(meta.override_usuario ?? []);
        const fresh = projectValue ? computePrefillFromAnalysis(projectValue) : { values: {} as ObraContext, deducidos: [] as string[] };
        const finalDeducidos = Array.from(new Set([
          ...(meta.deducido_de_obra_analysis ?? []),
          ...fresh.deducidos.filter((k) => !overrideSet.has(k)),
        ]));
        setContext({
          ...savedCtx,
          _meta: {
            ...meta,
            deducido_de_obra_analysis: finalDeducidos,
            override_usuario: Array.from(overrideSet),
          },
        });
        setHasExisting(true);
      } else if (projectValue) {
        // Pre-cargar del análisis del concurso
        prefillFromProjectAnalysis(projectValue, setContext);
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [projectId, recarga]);

  /** Aplica el análisis recién terminado sin pisar lo que la persona ya capturó. */
  function aplicarAnalisisNuevo(proj: Project) {
    const fresh = computePrefillFromAnalysis(proj);
    setContext((prev) => {
      const meta = (prev._meta ?? {}) as { deducido_de_obra_analysis?: string[]; override_usuario?: string[]; version_form?: string };
      const overrides = new Set(meta.override_usuario ?? []);
      const base: ObraContext = hasExistingRef.current ? { ...prev } : { ...fresh.values };
      if (!hasExistingRef.current) {
        for (const k of overrides) (base as Record<string, unknown>)[k] = (prev as Record<string, unknown>)[k];
      }
      return {
        ...base,
        _meta: {
          ...meta,
          version_form: meta.version_form ?? 'v3.0',
          deducido_de_obra_analysis: Array.from(new Set([
            ...(meta.deducido_de_obra_analysis ?? []),
            ...fresh.deducidos.filter((k) => !overrides.has(k)),
          ])),
          override_usuario: Array.from(overrides),
        },
      };
    });
  }
  const hasExistingRef = useRef(false);
  useEffect(() => { hasExistingRef.current = hasExisting; }, [hasExisting]);

  // ci: decidir y, si corresponde, arrancar la lectura de los documentos (UNA vez por entrada).
  const esCi = productoActivo() === 'ci';
  const decision = project
    ? decidirAnalisisAlEntrar({
        esCi,
        esEjemplo: project.isExample === true,
        tieneAnalisis: !!project.obraAnalysis,
        estadoResumen: project.summaryStatus,
        chunksTotal: project.chunksTotal ?? 0,
        archivos: files,
      })
    : null;

  async function arrancarLectura() {
    if (!projectId) return;
    setLecturaError(null);
    setLectura('leyendo');
    try {
      await regenerateSummary(projectId);
    } catch (e) {
      // 409 ALREADY_RUNNING = ya corre (otra pestaña): solo se muestra el avance.
      if (e instanceof ApiClientError && e.code === 'ALREADY_RUNNING') return;
      setLectura('fallo');
      setLecturaError(
        e instanceof ApiClientError && e.message
          ? e.message
          : 'No se pudieron leer los documentos. Puedes reintentar o capturar los datos a mano.',
      );
    }
  }

  useEffect(() => {
    if (loading || !decision) return;
    if (decision === 'en_curso') { setLectura((l) => (l === 'ninguna' ? 'leyendo' : l)); return; }
    if (decision === 'analizar' && !autoIntentado.current) {
      autoIntentado.current = true;
      void arrancarLectura();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decision, loading]);

  // Los documentos aún se están leyendo: se vuelve a mirar cada 4 s hasta que terminen.
  useEffect(() => {
    if (decision !== 'esperar') return;
    const t = setInterval(() => { recargaPrevia.current = 1; setRecarga((n) => n + 1); }, 4000);
    return () => clearInterval(t);
  }, [decision]);

  // Mientras el análisis corre se consulta su estado cada 3 s; al terminar se llena la pantalla.
  useEffect(() => {
    if (lectura !== 'leyendo' || !projectId) return;
    let vivo = true;
    const t = setInterval(() => {
      void getSummary(projectId).then((s) => {
        if (!vivo) return;
        if (s.status === 'ready') {
          setLectura('ninguna');
          recargaPrevia.current = 1;
          setRecarga((n) => n + 1);
        } else if (s.status === 'failed' || s.status === 'idle') {
          setLectura('fallo');
          setLecturaError('No se pudieron leer los documentos. Puedes reintentar o capturar los datos a mano.');
        }
      }).catch(() => { /* un fallo de red no corta la lectura: se vuelve a intentar */ });
    }, 3000);
    return () => { vivo = false; clearInterval(t); };
  }, [lectura, projectId]);

  function updateCtx<K extends keyof ObraContext>(key: K, value: ObraContext[K]) {
    setContext((prev) => {
      const meta = (prev._meta as { deducido_de_obra_analysis?: string[]; override_usuario?: string[] } | undefined) ?? {};
      const overrideArr = meta.override_usuario ?? [];
      return {
        ...prev,
        [key]: value,
        _meta: {
          ...meta,
          override_usuario: overrideArr.includes(String(key)) ? overrideArr : [...overrideArr, String(key)],
        },
      };
    });
  }

  /** Devuelve 'inferido' | 'tu decisión' según el meta del contexto. */
  function fieldOrigin(key: string): 'inferido' | 'captura' {
    const meta = (context._meta as { deducido_de_obra_analysis?: string[]; override_usuario?: string[] } | undefined);
    const isDeducido = meta?.deducido_de_obra_analysis?.includes(key) ?? false;
    const isOverride = meta?.override_usuario?.includes(key) ?? false;
    if (isDeducido && !isOverride) return 'inferido';
    return 'captura';
  }


  async function handleSave() {
    if (!projectId) return;
    setSaving(true);
    setError(null);
    try {
      // Persistir los defaults que la pantalla MUESTRA aunque el cotizador no toque
      // el campo: tipo de obra (pública), país (MX) y moneda (MXN). Sin esto se
      // guardaban vacíos y el APU-IA los perdía (país/moneda para precios de
      // mercado, tipo de obra para el modelo de costo del equipo).
      const contextToSave: ObraContext = {
        ...context,
        tipo_obra: context.tipo_obra ?? 'publica',
        // 'auto' = el modelo de costo del equipo se deriva del tipo de obra
        // (comportamiento histórico). El cotizador puede forzarlo desde la UI.
        modelo_costo_equipo: context.modelo_costo_equipo ?? 'auto',
        pais: context.pais ?? DEFAULT_PAIS,
        // La moneda se guarda de lo que el usuario eligió o de la sugerencia CON
        // EVIDENCIA que la pantalla mostró. Nunca un default.
        moneda: context.moneda ?? monedaSugerida ?? undefined,
      };
      if (!contextToSave.moneda) {
        setSaving(false);
        setError('Falta la moneda del presupuesto. Elígela en "Ubicación y condiciones ambientales": es la que decide en qué catálogos se buscan los precios.');
        return;
      }
      setContext(contextToSave);
      const res = await putObraContext(projectId, {
        context: contextToSave,
        // El cotizador NO administra factores (principio rector). Qué factores
        // existen/aplican lo deciden el motor (Ecosoft) + la IA por concepto.
        // Por eso no se manda selección por proyecto desde esta pantalla.
        factores_seleccion: [],
        source: hasExisting ? 'mixed' : 'manual',
      });
      // Si hubo cambio significativo y hay análisis con IA en el proyecto: dialog
      // explícito. Usamos `totalOutdated` (no `outdatedCount`) porque si los APUs
      // ya estaban marcados outdated por un cambio anterior, igual el Analista
      // necesita saber que SIGUEN desactualizados después de este nuevo cambio.
      const totalOutdated = res.totalOutdated ?? 0;
      if (res.hadSignificantChange && totalOutdated > 0) {
        setOutdatedDialog({ count: totalOutdated });
      } else {
        dispatchToast(
          <Toast><ToastTitle>Contexto de proyecto guardado</ToastTitle></Toast>,
          { intent: 'success', timeout: 3000 },
        );
        navigate(returnTo);
      }
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  }

  // Si el usuario llegó desde la cotización, el breadcrumb la incluye — sin
  // esto el único camino de regreso era el proyecto (reporte 2026-07-03).
  const cameFromQuote = /\/quotes\//.test(returnTo);
  const cameFromPlanos = /\/planos(\/|$|\?)/.test(returnTo);
  const obraCrumbs = [
    { label: 'Inicio', to: '/projects' },
    { label: 'Proyectos', to: '/projects' },
    { label: project?.name ?? 'Proyecto', to: `/project/${projectId}` },
    ...(cameFromQuote ? [{ label: 'Presupuesto', to: returnTo }] : []),
    ...(cameFromPlanos ? [{ label: 'Planos', to: returnTo }] : []),
    { label: 'Contexto de proyecto' },
  ];

  // Botones de acción reutilizados en la barra superior y en la inferior.
  // Mismos handlers y mismo estado (`saving`); `withDemo` marca solo la copia
  // canónica (la inferior) con el hook de demo para no duplicar el selector.
  const actionButtons = (withDemo: boolean) => (
    <>
      <Button appearance="subtle" onClick={() => navigate(returnTo)}>Cancelar</Button>
      <Button
        appearance="primary"
        icon={<Save24Regular />}
        onClick={() => void handleSave()}
        disabled={saving}
        {...(withDemo ? { 'data-demo': 'guardar-contexto' } : {})}
      >
        {saving ? 'Guardando…' : 'Guardar contexto'}
      </Button>
    </>
  );

  if (loading) {
    return (
      <div className={styles.page}>
        <AppTopBar />
        <Breadcrumbs items={obraCrumbs} />
        <div className={styles.container}><Spinner label="Cargando contexto…" /></div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <AppTopBar />
      <Breadcrumbs items={obraCrumbs} />
      <div className={styles.container}>

        <div className={styles.pageHead}>
          <div>
            <Text as="h1" className={styles.title}>Contexto de proyecto</Text>
            <Text className={styles.subtitle}>
              Se llena <strong style={{ color: 'var(--text-primary)' }}>una vez por proyecto</strong>. Aplica a todos los análisis de precios unitarios con IA que la inteligencia artificial cree para esta obra.
              {project && (
                <span className={styles.subtitleProject}>
                  <strong style={{ color: 'var(--text-primary)' }}>Proyecto:</strong> {project.name}
                </span>
              )}
            </Text>
          </div>
          <div className={styles.headActions}>
            {actionButtons(false)}
          </div>
        </div>

        {error && (
          <MessageBar intent="error" style={{ marginBottom: 16 }}>
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        {esCi && lectura === 'leyendo' && (
          <MessageBar intent="info" layout="multiline" style={{ marginBottom: 16 }}>
            <MessageBarBody>
              <Spinner size="tiny" style={{ display: 'inline-flex', marginRight: 8, verticalAlign: 'middle' }} />
              Leyendo los documentos del proyecto… Con lo que encuentre se llenarán los datos de la obra
              (ubicación, clima, tipo de obra). Puedes ir capturando mientras tanto.
            </MessageBarBody>
          </MessageBar>
        )}
        {esCi && lectura === 'fallo' && (
          <MessageBar intent="warning" layout="multiline" style={{ marginBottom: 16 }}>
            <MessageBarBody>{lecturaError}</MessageBarBody>
            <MessageBarActions>
              <Button size="small" appearance="primary" onClick={() => void arrancarLectura()}>Reintentar</Button>
            </MessageBarActions>
          </MessageBar>
        )}
        {analysisMissing && esCi && lectura === 'ninguna' && decision !== 'esperar' && (
          // En OPUS ci no hay resumen ejecutivo (es de OPUS ai): solo se piden los datos de la obra.
          <MessageBar intent="info" layout="multiline" style={{ marginBottom: 16 }}>
            <MessageBarBody>
              Captura los datos de la obra (altitud, clima, lluvia, horario de trabajo): con ellos se ajustan los
              rendimientos de los análisis de precio. Lo que no captures se queda con valores por omisión.
            </MessageBarBody>
          </MessageBar>
        )}
        {analysisMissing && productoActivo() !== 'ci' && (
          <MessageBar intent="warning" layout="multiline" style={{ marginBottom: 16 }}>
            <MessageBarBody>
              El <strong>resumen del proyecto</strong> aún no se ha generado, por eso no se
              pudieron inferir datos como el <strong>tipo de obra</strong>, la ubicación, la
              ciudad o el clima: los que ves son valores por omisión, no salieron de tus
              documentos. Genéralo para autocompletar, o captura los datos a mano.
            </MessageBarBody>
            <MessageBarActions>
              <Button
                size="small"
                appearance="primary"
                onClick={() =>
                  navigate(`/project/${projectId}`, {
                    state: { openResumen: true, autoGenerateSummary: true },
                  })
                }
              >
                Generar resumen
              </Button>
            </MessageBarActions>
          </MessageBar>
        )}

        {/* Leyenda de badges (spec 2026-07-02): hoy INFERIDO / TU DECISIÓN no
            se explicaban en ningún lugar de la interfaz. */}
        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <span className={styles.legendBadge} style={{ background: 'var(--accent-weak)', color: 'var(--accent)' }}>inferido</span>
            La inteligencia artificial lo leyó de los documentos del concurso; verifícalo y corrígelo si hace falta.
          </span>
          <span className={styles.legendItem}>
            <span className={styles.legendBadge} style={{ background: 'var(--privado-weak)', color: 'var(--privado)' }}>tu decisión</span>
            Solo tú conoces esta condición del sitio; la inteligencia artificial no puede inferirla.
          </span>
          <span className={styles.legendItem}>
            <span className={styles.legendHelpIcon} aria-hidden="true">?</span>
            Toca el signo de interrogación de cualquier campo para ver su guía.
          </span>
        </div>

        {/* ============ Sección 1: Tipo de obra y normativa ============ */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Tipo de obra y normativa</div>
          <div className={styles.grid}>
            <TaggedField label="Tipo de obra" fieldKey="tipo_obra" fieldOrigin={fieldOrigin}>
              <Dropdown
                value={context.tipo_obra === 'privada' ? 'Privada' : 'Pública (LOPSRM)'}
                selectedOptions={[context.tipo_obra ?? 'publica']}
                onOptionSelect={(_e, d) => updateCtx('tipo_obra', d.optionValue as 'publica' | 'privada')}
              >
                <Option value="publica">Pública (LOPSRM)</Option>
                <Option value="privada">Privada</Option>
              </Dropdown>
            </TaggedField>
            <TaggedField label="Modelo de costo de los equipos" fieldKey="modelo_costo_equipo" fieldOrigin={fieldOrigin}>
              <Dropdown
                value={MODELO_COSTO_EQUIPO_LABELS[context.modelo_costo_equipo ?? 'auto']}
                selectedOptions={[context.modelo_costo_equipo ?? 'auto']}
                onOptionSelect={(_e, d) => updateCtx('modelo_costo_equipo', d.optionValue as ObraContext['modelo_costo_equipo'])}
              >
                <Option value="auto">Según el tipo de obra</Option>
                <Option value="costo_horario">Costo horario</Option>
                <Option value="renta">Renta</Option>
              </Dropdown>
            </TaggedField>
            <TaggedField label="Disciplina de obra" fieldKey="disciplina" fieldOrigin={fieldOrigin}>
              <Dropdown
                placeholder="Selecciona…"
                value={context.disciplina ? DISCIPLINA_LABELS[context.disciplina] : ''}
                selectedOptions={context.disciplina ? [context.disciplina] : []}
                onOptionSelect={(_e, d) => updateCtx('disciplina', d.optionValue as ObraContext['disciplina'])}
              >
                <Option value="edificacion">Edificación</Option>
                <Option value="civil_pesada">Civil / obra pesada</Option>
                <Option value="industrial">Industrial</Option>
                <Option value="urbanizacion">Urbanización</Option>
                <Option value="hidraulica">Hidráulica / hidrosanitaria</Option>
                <Option value="electromecanica">Instalación electromecánica</Option>
                <Option value="mixta">Mixta</Option>
              </Dropdown>
            </TaggedField>
            {/* Separación §7 de la spec (decisión Sebastián 2026-07-03):
                `entidad` = convocante (lo que la IA ya infiere del pliego);
                `estado` = entidad federativa real del sitio (la necesita el
                motor: salarios regionales, impuesto sobre nómina). */}
            <TaggedField label="Entidad convocante" fieldKey="entidad" fieldOrigin={fieldOrigin}>
              <Input value={context.entidad ?? ''} onChange={(_e, d) => updateCtx('entidad', d.value)} placeholder="Ej. CONAGUA, UNAM, IMSS…" />
            </TaggedField>
            <TaggedField label="Entidad federativa (estado)" fieldKey="estado" fieldOrigin={fieldOrigin}>
              <Input value={context.estado ?? ''} onChange={(_e, d) => updateCtx('estado', d.value)} placeholder="Ej. Querétaro, Michoacán, CDMX…" />
            </TaggedField>
          </div>
        </div>

        {/* ============ Sección 2: Ubicación y clima ============ */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Ubicación y condiciones ambientales</div>
          <div className={styles.grid}>
            <TaggedField label="País" fieldKey="pais" fieldOrigin={fieldOrigin}>
              <IsoPicker
                value={context.pais}
                defaultCode={DEFAULT_PAIS}
                options={PAIS_OPTS}
                onSelect={(code) => updateCtx('pais', code)}
                placeholder="México"
              />
            </TaggedField>
            <TaggedField label="Moneda del presupuesto" fieldKey="moneda" fieldOrigin={fieldOrigin}>
              <IsoPicker
                value={context.moneda ?? monedaSugerida ?? undefined}
                defaultCode=""
                options={MONEDA_OPTS}
                onSelect={(code) => updateCtx('moneda', code)}
                placeholder="Elige la moneda"
              />
              {/* Por qué importa, dicho donde se decide: la moneda es la que define
                  en qué catálogos se buscan los precios. */}
              {faltaMoneda ? (
                <span style={{ fontSize: 11.5, color: 'var(--bad)' }}>
                  Elígela: es la moneda en la que se va a presentar el presupuesto y decide
                  en qué catálogos se buscan los precios.
                </span>
              ) : monedaDeCatalogos && !context.moneda ? (
                <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                  Tomada de tus catálogos de referencia. Cámbiala si el presupuesto va en otra moneda.
                </span>
              ) : null}
            </TaggedField>
            <TaggedField label="Ciudad" fieldKey="ciudad" fieldOrigin={fieldOrigin}>
              <Input value={context.ciudad ?? ''} onChange={(_e, d) => updateCtx('ciudad', d.value)} placeholder="Ej. Apaseo el Grande, Gto." />
            </TaggedField>
            <TaggedField label="Altitud (msnm)" fieldKey="altitud_msnm" fieldOrigin={fieldOrigin}>
              <Input type="number" min={0} value={String(context.altitud_msnm ?? '')} onChange={(_e, d) => updateCtx('altitud_msnm', Number(d.value) || 0)} placeholder="Ej. 1,880" />
            </TaggedField>
            <TaggedField label="Temperatura promedio jornada (°C)" fieldKey="temperatura_promedio_jornada_c" fieldOrigin={fieldOrigin}>
              <Input type="number" value={String(context.temperatura_promedio_jornada_c ?? '')} onChange={(_e, d) => updateCtx('temperatura_promedio_jornada_c', Number(d.value) || 0)} placeholder="Ej. 24" />
            </TaggedField>
            <TaggedField label="Humedad > 80%" fieldKey="humedad_mayor_80_pct" fieldOrigin={fieldOrigin}>
              <Switch checked={context.humedad_mayor_80_pct ?? false} onChange={(_e, d) => updateCtx('humedad_mayor_80_pct', d.checked)} />
            </TaggedField>
            <TaggedField label="Trabajo exterior en temporada de lluvias" fieldKey="trabajo_exterior_en_temporada_lluvias" fieldOrigin={fieldOrigin}>
              <Switch checked={context.trabajo_exterior_en_temporada_lluvias ?? false} onChange={(_e, d) => updateCtx('trabajo_exterior_en_temporada_lluvias', d.checked)} />
            </TaggedField>
          </div>
        </div>

        {/* ============ Sección 3: Sitio y operación ============ */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Sitio y operación</div>
          <div className={styles.grid}>
            <TaggedField label="Niveles sobre planta baja" fieldKey="nivel_sobre_planta_baja" fieldOrigin={fieldOrigin}>
              <Input type="number" value={String(context.nivel_sobre_planta_baja ?? '0')} onChange={(_e, d) => updateCtx('nivel_sobre_planta_baja', Number(d.value) || 0)} />
            </TaggedField>
            <TaggedField label="Acceso al frente" fieldKey="acceso" fieldOrigin={fieldOrigin}>
              <Dropdown
                value={context.acceso === 'restringido' ? 'Restringido' : 'Normal'}
                selectedOptions={[context.acceso ?? 'normal']}
                onOptionSelect={(_e, d) => updateCtx('acceso', d.optionValue as 'normal' | 'restringido')}
              >
                <Option value="normal">Vehicular libre</Option>
                <Option value="restringido">Restringido (calles angostas, sin frente, horario)</Option>
              </Dropdown>
            </TaggedField>
            <TaggedField label="Espacio de trabajo" fieldKey="espacio" fieldOrigin={fieldOrigin}>
              <Dropdown
                value={context.espacio === 'reducido' ? 'Reducido' : 'Normal'}
                selectedOptions={[context.espacio ?? 'normal']}
                onOptionSelect={(_e, d) => updateCtx('espacio', d.optionValue as 'normal' | 'reducido')}
              >
                <Option value="normal">Normal (frente ≥ 6 m²)</Option>
                <Option value="reducido">Reducido (&lt; 6 m²)</Option>
              </Dropdown>
            </TaggedField>
            <TaggedField label="Turno" fieldKey="turno" fieldOrigin={fieldOrigin}>
              <Dropdown
                value={context.turno === 'nocturno' ? 'Nocturno' : context.turno === 'extra_continuo' ? 'Extra continuo' : 'Diurno'}
                selectedOptions={[context.turno ?? 'diurno']}
                onOptionSelect={(_e, d) => updateCtx('turno', d.optionValue as 'diurno' | 'nocturno' | 'extra_continuo')}
              >
                <Option value="diurno">Diurno (8 hrs)</Option>
                <Option value="nocturno">Nocturno (≥ 20:00 hrs)</Option>
                <Option value="extra_continuo">Extra continuo (&gt; 10h sostenido)</Option>
              </Dropdown>
            </TaggedField>
            <TaggedField label="Supervisión" fieldKey="supervision" fieldOrigin={fieldOrigin}>
              <Dropdown
                value={context.supervision === 'parcial' ? 'Parcial' : context.supervision === 'ausente' ? 'Ausente' : 'Permanente'}
                selectedOptions={[context.supervision ?? 'permanente']}
                onOptionSelect={(_e, d) => updateCtx('supervision', d.optionValue as 'permanente' | 'parcial' | 'ausente')}
              >
                <Option value="permanente">Permanente (maestro presente)</Option>
                <Option value="parcial">Parcial</Option>
                <Option value="ausente">Ausente</Option>
              </Dropdown>
            </TaggedField>
            <TaggedField label="Experiencia cuadrilla" fieldKey="experiencia_cuadrilla" fieldOrigin={fieldOrigin}>
              <Dropdown
                value={context.experiencia_cuadrilla === 'mixta' ? 'Mixta' : context.experiencia_cuadrilla === 'nueva' ? 'Nueva' : 'Experimentada con maestro'}
                selectedOptions={[context.experiencia_cuadrilla ?? 'exp_con_maestro']}
                onOptionSelect={(_e, d) => updateCtx('experiencia_cuadrilla', d.optionValue as 'exp_con_maestro' | 'mixta' | 'nueva')}
              >
                <Option value="exp_con_maestro">Experimentada con maestro</Option>
                <Option value="mixta">Mixta</Option>
                <Option value="nueva">Nueva sin maestro</Option>
              </Dropdown>
            </TaggedField>
            <TaggedField label="Geometría del proyecto" fieldKey="geometria" fieldOrigin={fieldOrigin}>
              <Dropdown
                value={context.geometria === 'irregular' ? 'Irregular' : 'Estándar'}
                selectedOptions={[context.geometria ?? 'estandar']}
                onOptionSelect={(_e, d) => updateCtx('geometria', d.optionValue as 'estandar' | 'irregular')}
              >
                <Option value="estandar">Estándar (ortogonal)</Option>
                <Option value="irregular">Irregular (curvas, doble altura, fachadas con quiebres)</Option>
              </Dropdown>
            </TaggedField>
            <TaggedField label="Horas de espera promedio por jornada" fieldKey="horas_espera_promedio_por_jornada" fieldOrigin={fieldOrigin}>
              {/* Acotado 0–3 (spec §7): arriba de 3 h el escenario real es un
                  turno restringido, no espera. */}
              <Input type="number" min={0} max={3} step="0.5" value={String(context.horas_espera_promedio_por_jornada ?? '0')} onChange={(_e, d) => updateCtx('horas_espera_promedio_por_jornada', Math.min(3, Math.max(0, Number(d.value) || 0)))} />
            </TaggedField>
          </div>
        </div>


        {/* ============ Acciones ============ */}
        <div className={styles.actions}>
          {actionButtons(true)}
        </div>
      </div>

      {/* Dialog post-guardado cuando hay análisis con IA que quedaron desactualizados */}
      <Dialog open={outdatedDialog !== null} onOpenChange={(_, d) => { if (!d.open) { setOutdatedDialog(null); navigate(returnTo); } }}>
        <DialogSurface style={{ maxWidth: 560 }}>
          <DialogBody>
            <DialogTitle>
              <span style={{ marginRight: 8 }}>⚠</span>
              Análisis de precios unitarios marcados como desactualizados
            </DialogTitle>
            <DialogContent style={{ fontSize: 14, lineHeight: 1.6 }}>
              <p>
                El contexto del proyecto cambió y <strong>{outdatedDialog?.count}</strong> análisis de precio unitario con IA{(outdatedDialog?.count ?? 0) > 1 ? 's' : ''} {(outdatedDialog?.count ?? 0) > 1 ? 'fueron' : 'fue'} marcado{(outdatedDialog?.count ?? 0) > 1 ? 's' : ''} como desactualizado{(outdatedDialog?.count ?? 0) > 1 ? 's' : ''}.
              </p>
              <p style={{ marginTop: 10, color: tokens.colorNeutralForeground3 }}>
                Estos análisis de precios unitarios se crearon con valores anteriores del contexto. Vas a verlos con un badge ⚠ amarillo en el listado. Para tener los precios y factores actualizados, vuelve a crearlos desde el presupuesto.
              </p>
              <p style={{ marginTop: 10, color: tokens.colorNeutralForeground3 }}>
                Las matrices que ya habías aprobado se mantienen aprobadas — solo se marcan como desactualizadas. Si las regeneras, vuelven a estado <em>pendiente</em>.
              </p>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                onClick={() => {
                  setOutdatedDialog(null);
                  navigate(returnTo);
                }}
              >
                Entendido
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}

// =============================================================
// Helpers
// =============================================================

/**
 * Field con badge "inferido" o "tu decisión" según el origen del valor.
 * Visual sutil pegado al label.
 */
function TaggedField(
  { label, origin, fieldKey, fieldOrigin, className, children, helpKey }: {
    label: string;
    /** Si se provee directamente, se usa. Sino se resuelve con fieldOrigin(fieldKey). */
    origin?: 'inferido' | 'captura';
    fieldKey?: string;
    fieldOrigin?: (key: string) => 'inferido' | 'captura';
    className?: string;
    children: React.ReactNode;
    /** Clave del texto de ayuda contextual; default = fieldKey (spec 2026-07-02). */
    helpKey?: string;
  },
) {
  const resolved: 'inferido' | 'captura' = origin
    ?? (fieldKey && fieldOrigin ? fieldOrigin(fieldKey) : 'captura');
  const isInferido = resolved === 'inferido';
  const tagStyle: React.CSSProperties = {
    display: 'inline-block',
    marginLeft: 8,
    fontSize: '9.5px',
    fontWeight: 700,
    padding: '2px 7px',
    borderRadius: '999px',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    background: isInferido ? 'var(--accent-weak)' : 'var(--privado-weak)',
    color: isInferido ? 'var(--accent)' : 'var(--privado)',
  };
  return (
    <Field
      className={className}
      label={(
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span>
            {label}
            <span style={tagStyle}>
              {isInferido ? 'inferido' : 'tu decisión'}
            </span>
          </span>
          <FieldHelp helpKey={helpKey ?? fieldKey ?? ''} />
        </span>
      )}
    >
      {children}
    </Field>
  );
}

/** Etiquetas legibles del modelo de costo de los equipos (para el Dropdown). */
const MODELO_COSTO_EQUIPO_LABELS: Record<string, string> = {
  auto: 'Según el tipo de obra',
  costo_horario: 'Costo horario',
  renta: 'Renta',
};

/** Etiquetas legibles de la disciplina de obra (para el Dropdown). */
const DISCIPLINA_LABELS: Record<string, string> = {
  edificacion: 'Edificación',
  civil_pesada: 'Civil / obra pesada',
  industrial: 'Industrial',
  urbanizacion: 'Urbanización',
  hidraulica: 'Hidráulica / hidrosanitaria',
  electromecanica: 'Instalación electromecánica',
  mixta: 'Mixta',
};

/**
 * Devuelve los nombres de campos del contexto que pueden inferirse del análisis
 * de obra del proyecto. Se usa tanto al cargar un contexto existente (re-tag
 * los campos como "inferido" si vienen del análisis) como al hacer prefill.
 */
/** Forma (parcial) del objeto `ai` dentro de `project.obraAnalysis`. */
type ObraAnalysisAi = {
  tipoLicitacion?: string;
  tipoObra?: 'publica' | 'privada' | null;
  entidad?: string;
  ubicacionPais?: string;
  ubicacionEstado?: string;
  ubicacionCiudad?: string;
  moneda?: string;
  disciplina?: string;
  climaEstimado?: {
    altitud_msnm?: number | null;
    temperatura_promedio_jornada_c?: number | null;
    humedad_alta?: boolean | null;
    temporada_lluvias_marcada?: boolean | null;
  } | null;
};

/**
 * Calcula (sin setear) los valores del contexto inferidos del análisis del
 * concurso + la lista de campos deducidos. Reutilizable para el pre-llenado
 * inicial y para el REFRESCO de campos inferidos al recargar (Q2a).
 */
function computePrefillFromAnalysis(project: Project): { values: ObraContext; deducidos: string[] } {
  const obra = (project as Project & { obraAnalysis?: Record<string, unknown> | null }).obraAnalysis;
  if (!obra) return { values: {}, deducidos: [] };
  const initial: ObraContext = {};
  const deducidos: string[] = [];
  const ai = (obra as { ai?: ObraAnalysisAi }).ai;
  // Pública o privada, EN LAS DOS DIRECCIONES. Antes solo existía la rama de "pública"
  // —se deducía de `tipoLicitacion`, que es el PROCEDIMIENTO— así que una obra privada
  // nunca se podía inferir y caía en el default pública, con la insignia "tu decisión"
  // puesta sobre un valor que nadie eligió. El caso que lo destapó (proyecto T1) tiene el
  // dato escrito en el documento: "Tipo de obra Privada (contratación directa entre
  // particulares)". Ahora el análisis lo trae explícito en `tipoObra`; `tipoLicitacion`
  // queda como respaldo para los pliegos ya analizados con la versión anterior.
  const tipoObra = ai?.tipoObra
    ?? (/p[úu]blica/i.test(ai?.tipoLicitacion ?? '') ? 'publica' as const : undefined);
  if (tipoObra) {
    initial.tipo_obra = tipoObra;
    deducidos.push('tipo_obra');
    // El marco normativo de obra pública (LOPSRM) NO aplica a una obra privada.
    if (tipoObra === 'publica') {
      initial.marco_normativo = 'LOPSRM';
      deducidos.push('marco_normativo');
    }
  }
  if (ai?.entidad) {
    initial.entidad = ai.entidad;
    deducidos.push('entidad');
  }
  // País del sitio (ISO alpha-2) — NO asumir México. Ancla moneda y país de
  // búsqueda de precios de mercado. Si el análisis no lo trae, se deja el default.
  if (ai?.ubicacionPais && /^[A-Za-z]{2}$/.test(ai.ubicacionPais)) {
    initial.pais = ai.ubicacionPais.toUpperCase();
    deducidos.push('pais');
  }
  // Moneda del presupuesto (ISO 4217, ej. 'ARS', 'MXN') si el pliego la declara.
  if (ai?.moneda && /^[A-Za-z]{3}$/.test(ai.moneda)) {
    initial.moneda = ai.moneda.toUpperCase();
    deducidos.push('moneda');
  }
  if (ai?.ubicacionEstado) {
    initial.estado = ai.ubicacionEstado;
    deducidos.push('estado');
  }
  if (ai?.ubicacionCiudad) {
    initial.ciudad = ai.ubicacionCiudad;
    deducidos.push('ciudad');
  }
  const clima = ai?.climaEstimado;
  if (clima) {
    if (typeof clima.altitud_msnm === 'number') {
      initial.altitud_msnm = clima.altitud_msnm;
      deducidos.push('altitud_msnm');
    }
    if (typeof clima.temperatura_promedio_jornada_c === 'number') {
      initial.temperatura_promedio_jornada_c = clima.temperatura_promedio_jornada_c;
      deducidos.push('temperatura_promedio_jornada_c');
    }
    if (typeof clima.humedad_alta === 'boolean') {
      initial.humedad_mayor_80_pct = clima.humedad_alta;
      deducidos.push('humedad_mayor_80_pct');
    }
    if (typeof clima.temporada_lluvias_marcada === 'boolean') {
      initial.trabajo_exterior_en_temporada_lluvias = clima.temporada_lluvias_marcada;
      deducidos.push('trabajo_exterior_en_temporada_lluvias');
    }
  }
  if (ai?.disciplina && ['edificacion', 'civil_pesada', 'industrial', 'urbanizacion'].includes(ai.disciplina)) {
    initial.disciplina = ai.disciplina as ObraContext['disciplina'];
    deducidos.push('disciplina');
  }
  return { values: initial, deducidos };
}

function prefillFromProjectAnalysis(project: Project, setCtx: (c: ObraContext) => void): void {
  const { values, deducidos } = computePrefillFromAnalysis(project);
  if (deducidos.length > 0) {
    values._meta = { deducido_de_obra_analysis: deducidos, version_form: 'v3.0' };
  }
  setCtx(values);
}
